import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Check, Plus, Trash } from "@phosphor-icons/react";
import {
  FIELD_TYPES,
  NUMBER_FORMATS,
  type BaseField,
  type FieldType,
  type NumberFormat,
} from "./types";
import { useTranslation } from "@/features/utils/i18n";

interface FieldEditorProps {
  field: BaseField;
  index: number;
  onUpdate: (updates: Partial<BaseField>) => void;
  onRemove: () => void;
  onDone: () => void;
  canRemove: boolean;
}

export const FieldEditor = ({
  field,
  index,
  onUpdate,
  onRemove,
  onDone,
  canRemove,
}: FieldEditorProps) => {
  const { t } = useTranslation("fieldEditor");
  return (
    <div className="border rounded-lg p-4 space-y-3 bg-accent/30">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-3">
          {/* Field Number & Type */}
          <div className="flex items-center gap-4">
            <div className="text-sm font-medium text-muted-foreground">
              {t("fieldNumber", { number: index + 1 })}
            </div>
            <Select
              value={field.type}
              onValueChange={(value) => {
                const newType = value as FieldType;
                const updates: Partial<BaseField> = { type: newType };

                // Reset type-specific fields when changing type
                if (newType !== "select") {
                  updates.options = undefined;
                }
                if (newType !== "number") {
                  updates.min = undefined;
                  updates.max = undefined;
                  updates.numberFormat = undefined;
                }

                onUpdate(updates);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`types.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Field Label */}
          <div>
            <Label htmlFor={`field-${field.id}-label`}>{t("label")}</Label>
            <Input
              id={`field-${field.id}-label`}
              value={field.label}
              onChange={(e) => {
                const label = e.target.value;
                // Auto-generate ID from label
                const id = label
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "") // Remove accents
                  .replace(/[^a-z0-9]+/g, "_") // Replace non-alphanumeric with underscore
                  .replace(/^_+|_+$/g, ""); // Remove leading/trailing underscores
                onUpdate({ label, id: id || field.id });
              }}
              placeholder={t("labelPlaceholder")}
              required
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor={`field-${field.id}-description`}>
              {t("description")}
            </Label>
            <Textarea
              id={`field-${field.id}-description`}
              value={field.description || ""}
              onChange={(e) => onUpdate({ description: e.target.value })}
              placeholder={t("descriptionPlaceholder")}
              rows={2}
            />
          </div>

          {/* Type-specific fields */}
          {field.type === "select" && (
            <OptionsEditor field={field} onUpdate={onUpdate} />
          )}

          {field.type === "number" && (
            <NumberConstraintsEditor field={field} onUpdate={onUpdate} />
          )}
        </div>

        {/* Remove button */}
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="xsicon"
            onClick={onRemove}
            confirm={t("confirmDeleteShort")}
            className="text-destructive hover:text-destructive"
          >
            <Trash className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button type="button" onClick={onDone} size="sm">
          <Check className="h-4 w-4 mr-2" />
          {t("done")}
        </Button>
      </div>
    </div>
  );
};

// Options editor for select type
const OptionsEditor = ({
  field,
  onUpdate,
}: {
  field: BaseField;
  onUpdate: (updates: Partial<BaseField>) => void;
}) => {
  const { t } = useTranslation("fieldEditor");
  const options = field.options || [];

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onUpdate({ options: newOptions });
  };

  const removeOption = (index: number) => {
    onUpdate({ options: options.filter((_, i) => i !== index) });
  };

  const addOption = () => {
    onUpdate({ options: [...options, ""] });
  };

  return (
    <div className="space-y-2">
      <Label>{t("options")}</Label>
      {options.map((option, index) => (
        <div key={index} className="flex gap-2">
          <Input
            placeholder={t("optionPlaceholder")}
            value={option}
            onChange={(e) => updateOption(index, e.target.value)}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removeOption(index)}
            disabled={options.length === 1}
          >
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addOption}>
        <Plus className="h-4 w-4 mr-2" />
        {t("addOption")}
      </Button>
    </div>
  );
};

// Number constraints editor
const NumberConstraintsEditor = ({
  field,
  onUpdate,
}: {
  field: BaseField;
  onUpdate: (updates: Partial<BaseField>) => void;
}) => {
  const { t } = useTranslation("fieldEditor");
  return (
    <div className="space-y-3">
      {/* Number Format */}
      <div>
        <Label htmlFor={`field-${field.id}-format`}>{t("format")}</Label>
        <Select
          value={field.numberFormat || "number"}
          onValueChange={(value) =>
            onUpdate({ numberFormat: value as NumberFormat })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {NUMBER_FORMATS.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`numberFormats.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Min/Max */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor={`field-${field.id}-min`}>{t("minimum")}</Label>
          <Input
            id={`field-${field.id}-min`}
            type="number"
            value={field.min ?? ""}
            onChange={(e) =>
              onUpdate({
                min: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            placeholder={t("minPlaceholder")}
          />
        </div>
        <div>
          <Label htmlFor={`field-${field.id}-max`}>{t("maximum")}</Label>
          <Input
            id={`field-${field.id}-max`}
            type="number"
            value={field.max ?? ""}
            onChange={(e) =>
              onUpdate({
                max: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            placeholder={t("maxPlaceholder")}
          />
        </div>
      </div>
    </div>
  );
};
